import { Select } from '@thanx/uikit/select'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import type {
  LiveUpdatesTimerDirection,
  LiveUpdatesTriggerMode,
} from 'api/liveUpdates.types'
import CalendarClock from 'components/Icons/CalendarClock'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import {
  HEADER_HEIGHT_VAR,
  NAVBAR_HEIGHT,
} from 'scenes/LiveUpdates/useCollapsingHeader'
import { OloWebhookPanel } from 'utilities/oloWebhookStatus'
import OloWebhookConnectionStatus, {
  OloWebhookPendingState,
} from './OloWebhookConnectionStatus'

const t = buildTranslate('live_updates.card.signal')

// Card chrome (Figma): two equal-width rich radio cards, 1px border, 4px
// radius, ~16px padding, 16px gap. Selected = belizeHole border + a faint
// belizeHole tint; unselected = grey30 border on white.
const CARD_RADIUS = '4px'
const CARD_PADDING = '16px'
const CARD_GAP = '16px'
const ICON_TILE_SIZE = '32px'
const ICON_TILE_RADIUS = '8px'
const DISABLED_OPACITY = 0.6
// Half the row, as when it sits beside a second option.
const FIXED_SIGNAL_WIDTH = `calc(50% - ${CARD_GAP} / 2)`

// The Olo connection-status panel is attached to the bottom of the "Use the Olo
// event" card (Figma 1247:12244) while the connection is still pending: card and
// panel are siblings inside a wrapper, the panel flush against the card's bottom
// edge and matching its width. Once Olo is connected neither wrapper nor panel
// renders — the event card goes back to the bare treatment the timer card uses,
// and the page-level banner owns connection health.
//
// INTENTIONAL DEVIATION: in Figma the border belongs to the CARD alone (a full
// 4-sided stroke) and the panel below carries no border at all — just a fill
// hanging beneath it. Here the WRAPPER owns the border and radius, so the
// outline encloses card + panel as one unit and the card's bottom edge reads as
// a divider. Confirmed with Darren 7/27: the containment is clearer about which
// card the status belongs to. `overflow: hidden` clips the panel's fill to the
// wrapper's rounded corners.
//
// Card and panel are siblings rather than parent/child on purpose: the panel
// contains a Button, and interactive content inside a role="radio" is invalid
// ARIA — it would fold the link's label into the radio's accessible name.

// Fixed width for the offset Select, sized for its longest option
// ("30 minutes") rather than stretching to fill the row (Figma:
// config-timer-mock.png).
const OFFSET_SELECT_WIDTH = '170px'

// Mirrors HEADER_HEIGHT_VAR's own fallback (index.tsx's previewTop calc) for
// the same "header hasn't published a measured height yet" case.
const DEFAULT_HEADER_HEIGHT_PX = 131

// Bare lightning-bolt glyph (path lifted from components/Icons/Lightning,
// which bakes in its own tile background/color) so the tile color here can
// follow the event/timer treatment like TriggerBadge, matching CalendarClock's
// fillColor-prop convention. Decorative only — the wrapping tile carries
// aria-hidden.
const BoltGlyph = ({ fillColor }: { fillColor: string }) => (
  <svg width="14" height="18" viewBox="0 0 24 24" fill="none">
    <path
      d="M9.33341 18.6668L10.0001 14.0002H6.66675L12.6667 5.3335H14.0001L13.3334 10.6668H17.3334L10.6667 18.6668H9.33341Z"
      fill={fillColor}
    />
  </svg>
)

export interface SignalSelectorProps {
  triggerMode: LiveUpdatesTriggerMode
  timerOffset: number | null
  timerOffsetValues: number[]
  timerDirection: LiveUpdatesTimerDirection | null
  eventOptionLabel: string
  disabled?: boolean
  /** Present only when the event signal is sourced from Olo's webhook. While
   * its state is anything but 'connected' the event option carries a status
   * panel explaining why; once connected it renders bare, like the timer card.
   * Undefined for every other provider, and until the scene's queries settle
   * (nothing renders, and the two cards stay equal height). */
  oloWebhook?: OloWebhookPanel
  /** The event option can't be chosen: this status is Olo-signalled and the
   * webhook isn't confirmed delivering events yet. Deliberately NOT derived
   * from `oloWebhook` being absent — that is also how "this status isn't Olo
   * at all" is expressed, and while the connection queries are still in flight
   * an Olo status has no panel either. Collapsing the two would leave a window
   * during load where the event signal is selectable for a merchant who has
   * never configured a webhook. */
  eventOptionUnavailable?: boolean
  eventUnsupportedNotice?: string
  onChangeMode: (mode: LiveUpdatesTriggerMode) => void
  onChangeOffset: (minutes: number) => void
}

interface ModeOption {
  mode: LiveUpdatesTriggerMode
  label: string
  tileBg: string
  icon: React.ReactNode
}

// The offset dropdown's trailing copy depends on which side of "estimated
// ready time" this status sits on (preparing = before, ready = after).
// Extracted to a switch rather than a 3-way ternary chain per convention.
const getDirectionCopy = (direction: LiveUpdatesTimerDirection | null) => {
  switch (direction) {
    case 'before':
      return t('before_ready_time')
    case 'after':
      return t('after_ready_time')
    default:
      return null
  }
}

// What the attached panel needs, narrowed to the states that actually render
// one. Returning the pair (rather than a boolean) is what lets the panel's
// `connectionState` prop reject 'connected' at compile time.
interface PendingOloPanel {
  connectionState: OloWebhookPendingState
  onManage: () => void
}

const toPendingPanel = (
  panel: OloWebhookPanel | undefined
): PendingOloPanel | undefined =>
  panel && panel.connectionState !== 'connected'
    ? { connectionState: panel.connectionState, onManage: panel.onManage }
    : undefined

// Which signal drives a Live Updates trigger card: the provider's own event,
// or a timer offset from the provider's estimated ready time. Rendered as a
// custom radiogroup (role=radiogroup / role=radio) rather than the native
// Radio component: each option is a full clickable card, so the a11y contract
// is wired by hand (aria-checked + Enter/Space + arrow keys). Modeled on a
// prior SignalSourceSelector implementation, genericized from a single
// provider to the trigger_mode 'event' | 'timer' domain model.
const SignalSelector = ({
  triggerMode,
  timerOffset,
  timerOffsetValues,
  timerDirection,
  eventOptionLabel,
  disabled = false,
  oloWebhook,
  eventOptionUnavailable = false,
  eventUnsupportedNotice,
  onChangeMode,
  onChangeOffset,
}: SignalSelectorProps) => {
  const [css] = useStyletron()
  const cardRefs = useRef<
    Record<LiveUpdatesTriggerMode, HTMLDivElement | null>
  >({
    event: null,
    timer: null,
  })

  // The offset Select's menu is portaled to document.body and repositioned by
  // popper.js on scroll, but baseui never closes it on its own — left open it
  // rides along, floating over the sticky header/banner indefinitely. baseui
  // has no imperative close in this version, so closing it takes two steps:
  // blur the control so real DOM focus actually leaves it (its own .focus()
  // no-ops otherwise, permanently wedging the next open), then dispatch a
  // bubbling click at document.body so its document-level click-outside
  // listener — the only thing that updates its internal isOpen/isFocused
  // state once focus is no longer inside the anchor — sees a genuine outside
  // click and closes it. That click bubbles to every document-level click
  // listener in the app, not just baseui's — accepted, since baseui needs a
  // real outside click to update its state and there's no way to scope one
  // tighter; if another dropdown/tooltip happens to be open at the same
  // moment it may close too, which is the same thing a real outside click
  // would have done. The event is also untrusted (isTrusted: false), which
  // baseui doesn't currently check; if it ever starts filtering on that,
  // this stops closing the menu silently rather than erroring. Only wired
  // up while the menu is open, matching useCollapsingHeader's
  // passive/capture listener.
  const offsetFieldRef = useRef<HTMLDivElement | null>(null)
  const offsetControlRef = useRef<HTMLInputElement | HTMLDivElement | null>(
    null
  )
  const [isOffsetMenuOpen, setIsOffsetMenuOpen] = useState(false)

  const closeOffsetMenuIfHidden = useCallback(() => {
    const field = offsetFieldRef.current
    if (!field) return
    const { top, bottom } = field.getBoundingClientRect()
    // "No longer visible" means hidden behind the page's own opaque chrome,
    // not merely past the raw viewport edge: the scene's sticky header (FR-62,
    // docked below the fixed app nav) paints over anything that scrolls above
    // its bottom edge well before that content reaches y=0. Computed from the
    // same NAVBAR_HEIGHT/HEADER_HEIGHT_VAR contract index.tsx uses for its own
    // preview-pinning (previewTop), rather than querying index.tsx's DOM for
    // its header node — keeps this tied to that file through an actual
    // TS-visible import instead of a data-testid string a rename could break
    // silently, and avoids a per-scroll-tick document query.
    const rootFontSizePx =
      parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
    const navbarHeightPx = parseFloat(NAVBAR_HEIGHT) * rootFontSizePx
    const headerHeightPx =
      parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue(
          HEADER_HEIGHT_VAR
        )
      ) || DEFAULT_HEADER_HEIGHT_PX
    const hiddenBelow = navbarHeightPx + headerHeightPx
    if (bottom <= hiddenBelow || top >= window.innerHeight) {
      offsetControlRef.current?.blur()
      document.body.dispatchEvent(new MouseEvent('click', { bubbles: true }))
    }
  }, [])

  useEffect(() => {
    if (!isOffsetMenuOpen) return
    window.addEventListener('scroll', closeOffsetMenuIfHidden, {
      passive: true,
      capture: true,
    })
    return () =>
      window.removeEventListener('scroll', closeOffsetMenuIfHidden, true)
  }, [isOffsetMenuOpen, closeOffsetMenuIfHidden])

  // The event option is inert until Olo is actually sending us events —
  // selecting a signal we can't receive would configure a status that never
  // fires.
  const pendingOloPanel = toPendingPanel(oloWebhook)
  // The panel and the lock answer different questions and must not be merged:
  // the panel needs a RESOLVED unhealthy state to have copy to show, while the
  // lock must also hold during the unresolved window before that state arrives.
  const hasOloPanel = pendingOloPanel !== undefined
  const oloNotReady = eventOptionUnavailable || hasOloPanel

  const timerOption: ModeOption = {
    mode: 'timer',
    label: t('base_on_ready_time'),
    tileBg: palette.eggYolk10,
    icon: <CalendarClock fillColor={palette.eggYolk} />,
  }
  const eventOption: ModeOption = {
    // Green per Figma 1247:12247 (Nephritis-10 tile, Nephritis-50 glyph) —
    // matching TriggerBadge's 'event' pill. This palette's `spearmint` is
    // teal (#13C1BF), so it is not the token for the event treatment.
    mode: 'event',
    label: eventOptionLabel,
    tileBg: palette.nephritis10,
    icon: <BoltGlyph fillColor={palette.nephritis} />,
  }
  // Event leads (left) when it's usable — the normal, connected case. Once
  // Olo isn't ready the disabled event card moves behind the timer option
  // (right), per Figma 2651:8427, so the default/actionable choice leads.
  const options: ModeOption[] = oloNotReady
    ? [timerOption, eventOption]
    : [eventOption, timerOption]

  const isOptionDisabled = (index: number) =>
    disabled || (options[index].mode === 'event' && oloNotReady)

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    index: number
  ) => {
    if (isOptionDisabled(index)) return

    const isSelectKey = event.key === 'Enter' || event.key === ' '
    const isNextKey = event.key === 'ArrowRight' || event.key === 'ArrowDown'
    const isPrevKey = event.key === 'ArrowLeft' || event.key === 'ArrowUp'

    if (isSelectKey) {
      event.preventDefault()
      onChangeMode(options[index].mode)
      return
    }

    if (isNextKey || isPrevKey) {
      event.preventDefault()
      const delta = isNextKey ? 1 : -1
      const nextIndex = (index + delta + options.length) % options.length
      // With only two options, arrowing towards a disabled one has nowhere else
      // to go: stay put rather than moving focus onto an inert card.
      if (isOptionDisabled(nextIndex)) return
      const nextMode = options[nextIndex].mode
      onChangeMode(nextMode)
      cardRefs.current[nextMode]?.focus()
    }
  }

  const offsetOptions = timerOffsetValues.map(value => ({
    value: String(value),
    label:
      value === 1
        ? t('minutes_one', { count: value })
        : t('minutes_other', { count: value }),
  }))
  const selectedOffsetOption = offsetOptions.find(
    option => option.value === String(timerOffset)
  )
  const directionCopy = getDirectionCopy(timerDirection)

  const eventUnsupported = eventUnsupportedNotice !== undefined
  const showOffset = triggerMode === 'timer' || eventUnsupported
  const switchToTimerWithOffset = (minutes: number) => {
    if (triggerMode !== 'timer') onChangeMode('timer')
    onChangeOffset(minutes)
  }

  const renderIconTile = (option: ModeOption) => (
    <span
      aria-hidden="true"
      className={css({
        flexShrink: 0,
        width: ICON_TILE_SIZE,
        height: ICON_TILE_SIZE,
        borderRadius: ICON_TILE_RADIUS,
        backgroundColor: option.tileBg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      })}
    >
      {option.icon}
    </span>
  )

  // A status can carry a saved `trigger_mode` of 'event' from before the Olo
  // webhook was set up (it comes straight off the configuration payload, which
  // doesn't know the connection state). An unusable signal must not read as the
  // active choice, so the card renders unselected until the webhook is live.
  // Presentational only — nothing is written, and the selection comes back on
  // its own once the connection reaches 'connected'.
  const isOloEventUnavailable = (mode: LiveUpdatesTriggerMode) =>
    mode === 'event' && oloNotReady

  const selectedIndex = options.findIndex(
    option => option.mode === triggerMode && !isOloEventUnavailable(option.mode)
  )

  // Roving tabindex (WAI-ARIA radiogroup): one tab stop for the whole group, so
  // Tab enters/leaves it as a unit and arrow keys move within it. It follows the
  // checked option, falling back to the first — but never lands on a disabled
  // card while another one is still selectable, and disappears entirely (-1)
  // when every option is disabled.
  const preferredTabStop = selectedIndex === -1 ? 0 : selectedIndex
  const tabStopIndex = !isOptionDisabled(preferredTabStop)
    ? preferredTabStop
    : options.findIndex((_option, index) => !isOptionDisabled(index))

  return (
    <div
      className={css({ display: 'flex', flexDirection: 'column', gap: '16px' })}
    >
      {eventUnsupported ? (
        <div
          data-testid="lu-fixed-signal"
          className={css({
            width: FIXED_SIGNAL_WIDTH,
            padding: CARD_PADDING,
            backgroundColor: palette.belizeHole10,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            borderStyle: 'solid',
            borderWidth: '1px',
            borderRadius: CARD_RADIUS,
            borderColor: palette.belizeHole,
            opacity: disabled ? DISABLED_OPACITY : 1,
          })}
        >
          {renderIconTile(timerOption)}
          <Text.BodyText4 bold color="grey90">
            {timerOption.label}
          </Text.BodyText4>
        </div>
      ) : (
        <>
          <Text.BodyText4 bold color="grey90" className="m-0">
            {t('question')}
          </Text.BodyText4>
          <div
            role="radiogroup"
            aria-label={t('question')}
            className={css({
              display: 'flex',
              gap: CARD_GAP,
              // With a panel attached, only the Olo column grows: the timer card
              // keeps its natural height rather than stretching to match it.
              // Without one, stay on the default stretch so the two cards still
              // match heights when one label wraps to a second line.
              alignItems: hasOloPanel ? 'flex-start' : 'stretch',
            })}
          >
            {options.map((option, index) => {
              const selected =
                option.mode === triggerMode &&
                !isOloEventUnavailable(option.mode)
              const optionDisabled = isOptionDisabled(index)
              const rovingTabIndex = index === tabStopIndex ? 0 : -1
              // The Olo card carries the connection panel while the connection is
              // pending; when it does, the surrounding wrapper draws the
              // border/radius for both.
              const panel =
                option.mode === 'event' ? pendingOloPanel : undefined

              const card = (
                <div
                  ref={el => (cardRefs.current[option.mode] = el)}
                  role="radio"
                  aria-checked={selected}
                  aria-disabled={optionDisabled}
                  aria-label={option.label}
                  tabIndex={rovingTabIndex}
                  onClick={() => !optionDisabled && onChangeMode(option.mode)}
                  onKeyDown={event => handleKeyDown(event, index)}
                  className={css({
                    cursor: optionDisabled ? 'default' : 'pointer',
                    padding: CARD_PADDING,
                    backgroundColor: selected
                      ? palette.belizeHole10
                      : palette.white,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    // The card always dims itself when disabled, whether or not a
                    // panel is attached — the connection-status panel below it
                    // (badge/message/Set up CTA) stays at full opacity either way,
                    // since the user can still act on it even when the event
                    // option itself can't be selected.
                    opacity: optionDisabled ? DISABLED_OPACITY : 1,
                    // Standalone, the card draws its own chrome; wrapped, the
                    // wrapper owns the border/radius so the panel sits flush
                    // underneath it as one bordered unit.
                    ...(panel
                      ? {}
                      : {
                          flex: 1,
                          borderStyle: 'solid',
                          borderWidth: '1px',
                          borderRadius: CARD_RADIUS,
                          borderColor: selected
                            ? palette.belizeHole
                            : palette.grey30,
                        }),
                    ':focus-visible': {
                      outline: `2px solid ${palette.belizeHole}`,
                      outlineOffset: '2px',
                    },
                  })}
                >
                  {renderIconTile(option)}
                  <Text.BodyText4 bold color={selected ? 'grey90' : 'grey70'}>
                    {option.label}
                  </Text.BodyText4>
                </div>
              )

              if (!panel) return <Fragment key={option.mode}>{card}</Fragment>

              return (
                // role="presentation" so this purely visual wrapper doesn't come
                // between the radiogroup and the radio it owns.
                <div
                  key={option.mode}
                  role="presentation"
                  data-testid="lu-olo-signal-card"
                  className={css({
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    borderStyle: 'solid',
                    borderWidth: '1px',
                    borderRadius: CARD_RADIUS,
                    borderColor: selected ? palette.belizeHole : palette.grey30,
                    overflow: 'hidden',
                  })}
                >
                  {card}
                  <OloWebhookConnectionStatus
                    connectionState={panel.connectionState}
                    onManageClick={panel.onManage}
                  />
                </div>
              )
            })}
          </div>
        </>
      )}

      <Text.BodyText4 color="grey70">
        {eventUnsupportedNotice ?? t('help')}
      </Text.BodyText4>

      {showOffset && (
        <div
          className={css({
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          })}
        >
          <Text.BodyText4 bold color="grey90" className="m-0">
            {t('send_this_message')}
          </Text.BodyText4>
          <div
            className={css({
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            })}
          >
            <div
              ref={offsetFieldRef}
              data-testid="lu-offset-field"
              className={css({ width: OFFSET_SELECT_WIDTH, flexShrink: 0 })}
            >
              <Select
                aria-label={t('offset_aria_label')}
                options={offsetOptions}
                value={selectedOffsetOption ? [selectedOffsetOption] : []}
                onChange={selected => {
                  const [chosen] = selected
                  if (chosen) switchToTimerWithOffset(Number(chosen.value))
                }}
                disabled={disabled}
                controlRef={offsetControlRef}
                onOpen={() => setIsOffsetMenuOpen(true)}
                onClose={() => setIsOffsetMenuOpen(false)}
              />
            </div>
            {directionCopy && (
              <Text.BodyText4 color="grey70">{directionCopy}</Text.BodyText4>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default SignalSelector
